import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import renderSelectField from '../../../../shared/components/form/Select';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { renderFieldForm, renderTextArea } from '../../shared/formComponents';
import {
  DSP_ITEM_TYPE_OPTIONS,
  DSP_AD_FORMAT_OPTIONS,
  DSP_GENDER_OPTIONS,
  DSP_SPEND_TACTIC_OPTIONS,
  DSP_SPEND_SPEED_OPTIONS,
} from '../../shared/constants';

const DspBudgetFields = ({
  setupFlow,
  agencyInternal,
  masterItemOnList,
  masterItemOffList,
  itemCsvInput,
}) => {
  // URL validation helper
  const isValidUrl = (urlString) => {
    try {
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  return (
    <>
      <div className="form__form-group">
        <span className="form__form-group-label">
          DSP Item Type <Tooltip id="dspItemType" msg="Select the type of landing destination for this DSP budget" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field
            name="dspItemType"
            type="text"
            component={renderSelectField}
            options={DSP_ITEM_TYPE_OPTIONS}
          />
        </div>
      </div>

      <div className="form__form-group">
        <span className="form__form-group-label">
          Landing Page URL <Tooltip id="dspLandingPageUrl" msg="Enter the full URL where ad traffic will be directed" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field
            name="masterItemOnList"
            type="text"
            component={renderFieldForm}
            placeholder="https://www.amazon.com/..."
          />
        </div>
        {masterItemOnList && (() => {
          const urlValue = masterItemOnList.trim();
          const isValid = isValidUrl(urlValue);

          return (
            <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', border: `1px solid ${isValid ? 'rgba(70, 187, 253, 0.4)' : 'rgba(255, 0, 0, 0.4)'}`, backgroundColor: `${isValid ? 'rgba(70, 187, 253, 0.1)' : 'rgba(255, 0, 0, 0.1)'}` }}>
              <div style={{ fontWeight: '500', color: isValid ? '#70bbfd' : '#ff4861' }}>
                {isValid ? '✓ Valid URL' : '⚠ Invalid URL'}
              </div>
              {!isValid && urlValue && (
                <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                  Please enter a valid URL starting with http:// or https://
                </div>
              )}
            </div>
          );
        })()}
      </div>

      <div className="form__form-group">
        <span className="form__form-group-label">
          DSP Ad Format <Tooltip id="dspAdFormat" msg="Select Display or Video format for DSP ads" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field
            name="dspAdFormat"
            type="text"
            component={renderSelectField}
            options={DSP_AD_FORMAT_OPTIONS}
          />
        </div>
      </div>

      <div className="form__form-group">
        <span className="form__form-group-label">
          Gender Targeting <Tooltip id="dspGender" msg="Select gender targeting for this DSP campaign" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field
            name="dspGender"
            type="text"
            component={renderSelectField}
            options={DSP_GENDER_OPTIONS}
          />
        </div>
      </div>

      <div className="form__form-group">
        <span className="form__form-group-label">
          Full Targeting Goal <Tooltip id="dspSpendTactic" msg="Select the funnel strategy for this DSP budget" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field
            name="dspSpendTactic"
            type="text"
            component={renderSelectField}
            options={DSP_SPEND_TACTIC_OPTIONS}
          />
        </div>
      </div>

      {setupFlow?.value === 'advanced' && agencyInternal?.value && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            Inventory Allowed <Tooltip id="dspSpendSpeed" msg="Select the inventory allowed strategy for DSP" placement="top" />
          </span>
          <div className="form__form-group-field">
            <Field
              name="dspSpendSpeed"
              type="text"
              component={renderSelectField}
              options={DSP_SPEND_SPEED_OPTIONS}
            />
          </div>
        </div>
      )}

      <div className="form__form-group">
        <span className="form__form-group-label">
          Tracked ASINs <Tooltip id="masterItemOffList" msg="Enter ASINs to track for this DSP campaign in CSV format (comma-separated)" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field
            name="masterItemOffList"
            type="text"
            component={renderTextArea}
            format={itemCsvInput}
          />
        </div>
        {masterItemOffList && (() => {
          const itemList = (masterItemOffList || '').split(',').map(item => item.trim()).filter(item => item);
          const itemCount = itemList.length;
          const upperCaseItems = itemList.map(item => item.toUpperCase());
          const hasDuplicates = new Set(upperCaseItems).size !== itemList.length;
          const invalidAsins = itemList.filter(item => !item?.toUpperCase().startsWith('B0') || item?.length !== 10);

          if (hasDuplicates && itemCount > 0) {
            return (
              <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', border: '1px solid rgba(255, 165, 0, 0.4)', backgroundColor: 'rgba(255, 165, 0, 0.1)' }}>
                <div style={{ fontWeight: '500', color: '#ff8c00' }}>
                  ⚠ Duplicate ASINs found in list
                </div>
              </div>
            );
          }
          if (invalidAsins.length > 0) {
            return (
              <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', border: '1px solid rgba(255, 0, 0, 0.4)', backgroundColor: 'rgba(255, 0, 0, 0.1)' }}>
                <div style={{ marginBottom: '5px', fontWeight: '500', color: '#ff4861' }}>❌ Invalid ASINs (must start with B0 and be 10 characters):</div>
                <div style={{ fontSize: '0.9em', color: '#721c24' }}>
                  {invalidAsins.join(', ')}
                </div>
              </div>
            );
          }
          if (itemCount > 0) {
            return (
              <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', border: '1px solid rgba(70, 187, 253, 0.4)', backgroundColor: 'rgba(70, 187, 253, 0.1)' }}>
                <div style={{ fontWeight: '500', color: '#70bbfd' }}>
                  ✓ {itemCount} ASIN{itemCount !== 1 ? 's' : ''} tracked
                </div>
              </div>
            );
          }
          return null;
        })()}
      </div>

      <div className="form__form-group" style={{ padding: '15px', backgroundColor: 'rgba(70, 187, 253, 0.1)', borderRadius: '4px', marginTop: '15px' }}>
        <p style={{ margin: 0, fontSize: '0.9em', color: '#4a5568' }}>
          <strong>Note:</strong> DSP budgets do not use portfolio IDs or budget allocation percentages.
          The landing page URL, gender targeting, and tracked ASINs define the DSP campaign settings.
        </p>
      </div>
    </>
  );
};

DspBudgetFields.propTypes = {
  setupFlow: PropTypes.shape(),
  agencyInternal: PropTypes.shape(),
  masterItemOnList: PropTypes.string,
  masterItemOffList: PropTypes.string,
  itemCsvInput: PropTypes.func.isRequired,
};

DspBudgetFields.defaultProps = {
  setupFlow: null,
  agencyInternal: null,
  masterItemOnList: '',
  masterItemOffList: '',
};

export default DspBudgetFields;
